import { nowUtc } from '@/lib/clock';
import { isDesktop } from '@/platform';
import type { GentleTaskDatabase } from '@/data/db/database';
import { SETTINGS_ID } from '@/data/db/settings.schema';
import { mirrorApply, mirrorRebuild, mirrorSnapshot, mirrorStatus } from './mirror-bridge';
import type { MirrorStatus } from './mirror-bridge';
import { createMirrorHealth, type MirrorHealth, type MirrorHealthSnapshot } from './mirror-health';
import type { DirtyKey, MirrorObserver } from './mirror-middleware';
import { collectAll, collectChanges, restoreFromSnapshot } from './mirror-payload';

export * from './mirror-health';
export {
  attachDesktopCloseFlush,
  mirrorBackup,
  mirrorDataDir,
  mirrorExport,
  mirrorReadFile,
  mirrorStatus,
  openDataFolder,
  pickBackupDestination,
  pickRestoreSource,
  type MirrorSnapshot,
  type MirrorStatus,
} from './mirror-bridge';
export { restoreFromSnapshot, type RestoreReport } from './mirror-payload';

/**
 * 收集一小段時間內的變更再一起送（desktop-app.md 6.4）。
 *
 * 拖一張卡片會連續改動位置列與任務列，逐筆呼叫 Rust 會送出好幾次 IPC；
 * 這個延遲也順便讓「同一筆在一個交易裡被改好幾次」收斂成一次寫入。
 */
const FLUSH_DELAY_MS = 250;

export interface MirrorCoordinator {
  health: MirrorHealth;
  /** 立刻把待寫入的變更送出去。關窗前與匯出前呼叫。 */
  flush: () => Promise<void>;
  /** 整個重建鏡像。匯入的 replace-all 走這條。 */
  rebuild: () => Promise<void>;
  status: () => Promise<MirrorStatus | null>;
  resume: () => void;
  dispose: () => void;
}

/**
 * 把鏡像接到資料庫上。
 *
 * **網頁版會直接短路**：觀察者不掛、Tauri 套件不載入。
 * 呼叫端不需要自己判斷平台。
 */
export function installMirror(
  db: GentleTaskDatabase,
  onHealthChange?: (snapshot: MirrorHealthSnapshot) => void,
): MirrorCoordinator {
  const health = createMirrorHealth(onHealthChange);

  if (!isDesktop()) {
    return {
      health,
      flush: async () => {},
      rebuild: async () => {},
      status: async () => null,
      resume: () => {},
      dispose: () => {},
    };
  }

  /** key 是「表名 + 空白 + id」，避免兩張表的同名 id 互相蓋掉。 */
  const dirty = new Map<string, DirtyKey>();
  let needsRebuild = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let running: Promise<void> | null = null;
  let disposed = false;

  const keyOf = (key: DirtyKey) => `${key.table} ${key.id}`;

  // middleware 已經在 GentleTaskDatabase 的建構子註冊好了（必須在 open() 之前才攔得到），
  // 這裡只是把觀察者接上去。
  const observer: MirrorObserver = {
    onDirty: (keys) => {
      for (const key of keys) dirty.set(keyOf(key), key);
      schedule();
    },
    onBulkClear: () => {
      // table.clear() 沒辦法逐筆表達，整個重建比較安全。
      needsRebuild = true;
      schedule();
    },
  };
  db.mirrorObserver = observer;

  health.markAvailable();

  function schedule(): void {
    if (disposed) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      void flush().catch(() => {
        // 失敗已經記進 health，這裡吞掉只是避免 unhandled rejection。
      });
    }, FLUSH_DELAY_MS);
  }

  async function pushOnce(): Promise<void> {
    // dispose 之後還把待寫入的內容送出去，會在資料庫已經被刪掉之後才呼叫 Rust
    // （清除全部資料就是這個順序），寫回去的是一份已經不該存在的資料。
    if (disposed) return;

    if (needsRebuild) {
      needsRebuild = false;
      dirty.clear();
      health.markSyncing();
      try {
        const changes = await collectAll(db);
        const at = nowUtc();
        await mirrorRebuild(changes, at);
        health.markSuccess(at);
      } catch (error) {
        needsRebuild = true;
        health.markFailure(error);
        throw error;
      }
      return;
    }

    if (dirty.size === 0) return;
    const batch = [...dirty.values()];
    dirty.clear();

    health.markSyncing();
    try {
      const changes = await collectChanges(db, batch);
      const at = nowUtc();
      await mirrorApply(changes, at);
      health.markSuccess(at);
    } catch (error) {
      // 沒寫成功的 id 放回髒清單，下一次再試一次。
      // 不放回去的話，防毒鎖檔那一瞬間改的資料會永遠不在鏡像裡。
      for (const key of batch) dirty.set(keyOf(key), key);
      health.markFailure(error);
      throw error;
    }
  }

  async function flush(): Promise<void> {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    // 兩個 flush 同時跑會讓「放回髒清單」的順序變得難以推理，串起來就好。
    const previous = running ?? Promise.resolve();
    const next = previous.catch(() => {}).then(pushOnce);
    running = next.catch(() => {});
    await next;
  }

  async function rebuild(): Promise<void> {
    needsRebuild = true;
    await flush();
  }

  return {
    health,
    flush,
    rebuild,
    status: mirrorStatus,
    resume: () => {
      // React StrictMode replays effect cleanup/setup on mount. Reattach the
      // same observer instead of leaving this coordinator permanently disposed.
      disposed = false;
      db.mirrorObserver = observer;
      if (dirty.size > 0 || needsRebuild) schedule();
    },
    dispose: () => {
      disposed = true;
      if (db.mirrorObserver === observer) db.mirrorObserver = null;
      if (timer) clearTimeout(timer);
    },
  };
}

export interface AutoRestoreResult {
  restored: boolean;
  rowCount: number;
}

/** Repair a mirror whose last debounce was interrupted by a reload or crash. */
export async function repairMirrorIfBehind(
  db: GentleTaskDatabase,
  mirror: MirrorCoordinator,
): Promise<void> {
  const status = await mirror.status();
  if (!status) return;
  const settings = await db.settings.get(SETTINGS_ID);
  if (
    status.rowCount === 0 ||
    (settings?.lastDataMutationAt &&
      (!status.lastSyncAt || settings.lastDataMutationAt > status.lastSyncAt))
  )
    await mirror.rebuild();
}

/**
 * 空庫自動還原（desktop-app.md 第 8 節）。
 *
 * - IndexedDB 空 + `.db` 有資料：**自動靜默還原**。
 *   這是新電腦、重灌、WebView2 資料夾被清的情境，不可能判斷錯。
 * - IndexedDB 有資料：**絕不自動碰**，只在設定頁提供「從 .db 還原」。
 *
 * 刻意不做「比對時間戳自動提議」：時間戳在雙機、時區、或鏡像曾寫失敗的情況下
 * 會誤判，而且使用者會被彈窗訓練成無腦點確定，最後用舊資料蓋掉新資料。
 */
export async function autoRestoreIfEmpty(db: GentleTaskDatabase): Promise<AutoRestoreResult> {
  if (!isDesktop()) return { restored: false, rowCount: 0 };

  const settings = await db.settings.get(SETTINGS_ID);
  // seededAt 有值代表這台機器上已經有工作台，絕不自動碰。
  if (settings?.seededAt != null) return { restored: false, rowCount: 0 };
  if ((await db.tasks.count()) > 0) return { restored: false, rowCount: 0 };
  if ((await db.notes.count()) > 0) return { restored: false, rowCount: 0 };

  const status = await mirrorStatus();
  if (!status || !status.exists || status.rowCount === 0) {
    return { restored: false, rowCount: 0 };
  }

  const snapshot = await mirrorSnapshot();
  if (!snapshot || !hasRealContent(snapshot)) return { restored: false, rowCount: 0 };

  await restoreFromSnapshot(db, snapshot);
  return { restored: true, rowCount: status.rowCount };
}

/**
 * 全新安裝第一次開啟時，`ensureSettings` 會寫一列 settings，於是鏡像的 rowCount 就是 1。
 * 只看 rowCount 的話，第二次開啟會把「只有一列 settings」的鏡像當成有東西可以還原，
 * 白跑一次清空再寫回。所以要求快照裡真的有 settings 以外的內容。
 */
function hasRealContent(snapshot: {
  tables: Record<string, string[]>;
  attachments: unknown[];
}): boolean {
  if (snapshot.attachments.length > 0) return true;
  return Object.entries(snapshot.tables).some(
    ([table, rows]) => table !== 'settings' && rows.length > 0,
  );
}
